import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import {
  loadPlaylist,
  updatePlaylist,
} from '@disco/state-management/actions/playlist-actions'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import PlaylistGridItem from 'components/common/playlist-grid-item'
import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ChoiceList from 'components/ui/choice-list'
import ColorInput from 'components/ui/color-input'
import FileSelector from 'components/ui/file-selector'
import Grid from 'components/ui/grid'
import Label from 'components/ui/label'
import SidebarModal from 'components/ui/sidebar-modal'

const getSelectedView = (playlist: Playlist) => {
  if (playlist['color']) {
    return 'color'
  }

  if (playlist['image_url']) {
    return 'image'
  }

  return 'default'
}

const LibrariesLibraryPlaylistsPlaylist = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams<{ playlistId: string; libraryId: string }>()
  const playlistId: number = parseInt(params.playlistId!, 10)
  const playlist: Playlist = useSelector((state: State) =>
    getPlaylist(state, {
      id: playlistId,
    }),
  )
  useEffect(() => {
    dispatch(loadPlaylist(playlistId))
  }, [playlistId])
  const [selectedView, setSelectedView] = useState(getSelectedView(playlist))
  const [color, setColor] = useState(
    playlist && playlist['color'] ? playlist['color'] : '#00D084',
  )
  const [image, setImage] = useState<File | null | undefined>(null)
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(null)

  // useEffect(() => {
  //   if (playlist) {
  //     setSelectedView(getSelectedView(playlist))
  //     if (playlist['color']) {
  //       setColor(playlist['color'])
  //     }
  //   }
  // }, [playlist])
  const handleImageChange = (files: File[] | null | undefined) => {
    const file = files ? files[0] : null
    setImage(file)
  }

  const backPath = `/catalogs/${params.libraryId}/playlists`

  const handleCancel = () => navigate(backPath)

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(
      updatePlaylist(playlistId, {
        color: ['default', 'image'].includes(selectedView) ? null : color,
        image: ['default', 'color'].includes(selectedView) ? null : image,
      }),
    )
    navigate(backPath)
  }

  const renderContent = () => {
    if (selectedView === 'default') {
      return <PlaylistGridItem color={null} image={null} playlist={playlist} />
    }

    if (selectedView === 'color') {
      return (
        <>
          <Row columnsSize={[12]}>
            <div>
              <Label caption="Select a color for your playlist cover" />
              <ColorInput
                name="style.color"
                value={color}
                onChange={color => setColor(color.hex)}
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <PlaylistGridItem
                color={color}
                image={null}
                playlist={playlist}
              />
            </div>
          </Row>
        </>
      )
    }

    if (selectedView === 'image') {
      return (
        <>
          <Row columnsSize={[12]}>
            <div>
              <Label
                caption={
                  <>
                    Select an image for your playlist cover
                    <br />
                    (recommended size: 1500 x 1000px)
                  </>
                }
              />
              <FileSelector
                caption={[
                  'Drag new or existing images here, or click to browse your computer',
                  'Click to change image',
                ]}
                image={playlist && playlist['image_url']}
                showPreview
                type="image"
                onDrop={handleImageChange}
                onPreview={setImageUrl}
                height={132}
                dnd
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <PlaylistGridItem
                color={null}
                image={imageUrl || (playlist && playlist['image_url']) || null}
                playlist={playlist}
              />
            </div>
          </Row>
        </>
      )
    }

    return null
  }

  return (
    <SidebarModal
      title="Playlist design"
      sidebar={
        <ChoiceList
          title="Choose design view for playlist"
          multiple={false}
          items={[
            {
              label: 'Default',
              value: 'default',
            },
            {
              label: 'Choose a color',
              value: 'color',
            },
            {
              label: 'Choose an image',
              value: 'image',
            },
          ]}
          selected={selectedView}
          onChange={(value: string) => setSelectedView(value)}
        />
      }
      content={<Grid size="large">{renderContent()}</Grid>}
      actions={
        <ButtonContainer>
          <Button appearance="link" caption="Cancel" onClick={handleCancel} />
          <Button
            appearance="primary"
            caption="Confirm"
            onClick={handleSubmit}
          />
        </ButtonContainer>
      }
      onClose={handleCancel}
    />
  )
}

export default LibrariesLibraryPlaylistsPlaylist
